import { InformationCircle } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import { cn } from 'src/utils/classNamesHelper';
import { COUNTRIES, useTaxIdDescription } from 'src/utils/countryList';

import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import type { InputProps } from './Input';
import { Input } from './Input';
import { Tooltip } from './Tooltip';

interface InputTaxIdProps {
  country?: string;
}

export function InputTaxId({
  country,
  className,
  ...props
}: InputTaxIdProps & Partial<InputProps>) {
  const { t, i18n } = useLingui();
  const countryData = COUNTRIES.find((c) => c.value === country);
  const taxIds = Object.values(countryData?.taxIds || {});
  const getTaxIdLabel = useTaxIdDescription();

  const formattedTaxIdList = taxIds.map((t) => getTaxIdLabel(t.id)).slice(0, 2);
  const numberOfExtraTaxIds = taxIds.length - 2;
  const formattedOthers = plural(numberOfExtraTaxIds, {
    one: '# other',
    other: '# others',
  });

  const items =
    numberOfExtraTaxIds > 0
      ? [...formattedTaxIdList, formattedOthers]
      : formattedTaxIdList;

  const taxIdList = new Intl.ListFormat(i18n.locale, {
    type: 'disjunction',
  }).format(items);

  return (
    <Input
      data-cy="billing-tax-id"
      name="VAT"
      label={t`Tax ID number`}
      className={cn('min-w-0', className)}
      placeholder={`${taxIds
        .slice(0, 4)
        .map((taxId) => taxId.example)
        .join(', ')}${taxIds.length > 4 ? ', ...' : ''}`}
      description={
        taxIds[0] && (
          <div className="flex w-full justify-between">
            <Typography
              variant="interface-xs"
              className="my-2 truncate leading-125"
            >
              {taxIdList}
            </Typography>
            {taxIds.length > 1 && (
              <Tooltip
                clickable={true}
                title={
                  <span className="text-white">
                    <ul>
                      {taxIds.map((taxId) => (
                        <li key={taxId.id}>
                          <Typography
                            variant="interface-sm"
                            leadingNone
                            className="text-white"
                          >
                            {getTaxIdLabel(taxId.id)} - {taxId.example}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </span>
                }
              >
                <InformationCircle size={20} className="shrink-0" />
              </Tooltip>
            )}
          </div>
        )
      }
      {...props}
    />
  );
}
